import animateScrollTo from 'animated-scroll-to';
import * as React from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { Loader, Progress } from 'semantic-ui-react';

import { setFieldOnFocus } from '../../actions/AlphasPropertiesAction';
import SelectedAlphas from '../../containers/SelectedAlphas/SelectedAlphas';
import { localeData } from '../../locales/data';
import { StateType } from '../../reducers';
import {
  alphaPropertiesFieldOnFocusSelector,
  alphaPropertiesShouldScrollToSelector,
} from '../../reducers/AlphaPropertiesReducer/AlphaPropertiesSelector';
import { activeTabIndexSelector } from '../../reducers/EditorReducer/EditorSelector';
import { flashMessagesSimulationAlphasMessageSelector } from '../../reducers/FlashMessageReducer/FlashMessageSelector';
import { alphasSelectionCodeSelector } from '../../reducers/SimulationReducer/DataReducer/DataSelector';
import { FormattedMessageHelper } from '../../shared/helpers/internationalization';
import { DispatchConnect } from '../../types';
import AlphasDetailsContent from '../Alphas/AlphasDetails/Content/AlphasDetailsContent';
import AlphaProperties from '../AlphaProperties/AlphaProperties';
import FlashMessages from '../FlashMessages/FlashMessages';

import './SimulationVisualization.scss';
import { SimulationTip } from './SimulationTip/SimulationTip';

export interface SimulationVisualizationProps {
  simulatePercentage: number;
  fetchingPercentage: boolean;
  fetchingPost: boolean;
  data: any;
  handlerCancelSimulation: () => void;
  isFetching: boolean;
  path: string[];
  showCode: boolean;
  showLearn: boolean;
  shouldResize: number;
  currentChildActiveTab: number | null;
  currentActiveTab: number;
  isMultiTab: boolean;
  size?: number;
}

interface SimulationVisualizationDispatchProps {
  dispatchSetFieldOnFocus: (name: string | null) => void;
}

interface SimulationVisualizationStateProps {
  flashMessages: any;
  activeTab?: any;
  shouldScrollToAlphaProperties: boolean;
  alphasFromSelectionCode?: any;
  fieldOnFocus: null | string;
}

export type SimulationVisualizationPropsGeneric = SimulationVisualizationProps &
  SimulationVisualizationDispatchProps &
  SimulationVisualizationStateProps;

export class SimulationVisualization extends React.Component<SimulationVisualizationPropsGeneric> {
  intervalChart: any;

  componentDidUpdate(prevProps: SimulationVisualizationPropsGeneric) {
    if (
      (prevProps.flashMessages && !prevProps.flashMessages.equals(this.props.flashMessages)) ||
      this.props.activeTab !== prevProps.activeTab ||
      (!prevProps.flashMessages && this.props.flashMessages)
    ) {
      const elemScroll: any = document.getElementById('alphas-details-sections');
      const footer: any = document.querySelector('.alphas__footer-messages');

      const editorChildTabs = document.getElementById('editor-child-tabs');
      const editorChildTabsHeight: number = editorChildTabs ? editorChildTabs.clientHeight : 0;
      if (elemScroll && footer) {
        const flashHeight: number = footer ? footer.clientHeight : 0;
        // 140 represent the bottom bar height plus the top bar plus the settings
        elemScroll.style['max-height'] = `calc(var(--full-dvh) - ${flashHeight + editorChildTabsHeight + 140}px)`;
      }
    }
    if (prevProps.shouldScrollToAlphaProperties && !this.props.shouldScrollToAlphaProperties) {
      clearInterval(this.intervalChart);
      this.props.dispatchSetFieldOnFocus(null);
    }
    if (
      (!prevProps.fetchingPost && this.props.fetchingPost && this.props.shouldScrollToAlphaProperties) ||
      (prevProps.shouldScrollToAlphaProperties && this.props.shouldScrollToAlphaProperties && !this.props.data)
    ) {
      clearInterval(this.intervalChart);
      this.intervalChart = setInterval(this.checkIfChartExists, 1000);
    }
  }

  checkIfChartExists = () => {
    const sectionToScroll: any = document.getElementById('alphas-properties');
    const elemScroll: any = document.getElementById('alphas-details-sections');
    const chart = document.querySelector('.highcharts-root');
    if (
      chart &&
      sectionToScroll &&
      elemScroll &&
      (this.props.fieldOnFocus === 'tags' || this.props.fieldOnFocus === 'category' || this.props.fieldOnFocus === 'color')
    ) {
      clearInterval(this.intervalChart);
      animateScrollTo(sectionToScroll.offsetTop, { element: elemScroll, speed: 100, minDuration: 50 });
    }
  };

  render() {
    const {
      fetchingPost,
      simulatePercentage,
      fetchingPercentage,
      data,
      handlerCancelSimulation,
      path,
      showCode,
      showLearn,
      shouldResize,
      currentActiveTab,
      currentChildActiveTab,
      isMultiTab,
    } = this.props;
    if (fetchingPercentage) {
      return (
        <div className="simulation__results">
          <div className="simulation__progress">
            <div className="simulation__progress-content">
              <Progress
                className="intro-step-6 simulation__bar"
                percent={Math.round(simulatePercentage * 100).toString()}
                progress="percent"
              />
              <div className="simulation__cancel">
                <FormattedMessageHelper id="simulationVisualization.takesTime" />
                <br />
                <FormattedMessageHelper
                  tagName="span"
                  id="simulationVisualization.1"
                  defaultMessage={localeData.en['simulationVisualization.1']}
                />
                <span onClick={handlerCancelSimulation} className="simulation__cancel--here">
                  <FormattedMessageHelper
                    tagName="span"
                    id="simulationVisualization.2"
                    defaultMessage={localeData.en['simulationVisualization.2']}
                  />
                </span>
                <FormattedMessageHelper
                  tagName="span"
                  id="simulationVisualization.3"
                  defaultMessage={localeData.en['simulationVisualization.3']}
                />
              </div>
              <SimulationTip />
            </div>
          </div>
          <AlphaProperties
            path={path}
            tabIndex={currentActiveTab}
            indexChild={currentChildActiveTab}
            isMultiTab={isMultiTab}
            isSimulate={true}
            id="alphas-properties"
          />
        </div>
      );
    } else if (fetchingPost) {
      return (
        <div className="simulation__results">
          <div className="simulation__progress">
            <div className="loader">
              <Loader active={true} inline="centered" size="massive" />
            </div>
          </div>
          <AlphaProperties
            path={path}
            tabIndex={currentActiveTab}
            indexChild={currentChildActiveTab}
            isMultiTab={isMultiTab}
            isSimulate={true}
            id="alphas-properties"
          />
        </div>
      );
    } else if ((data === null || data === undefined) && this.props.alphasFromSelectionCode) {
      return (
        <div className="simulation__results">
          <SelectedAlphas
            path={path}
            id="alphas-selectedAlphas"
            alphaSelectionsPath="alphasFromSelectionCode"
            fetchingAlphaSelectionsPath="selectionCodeFetching"
          />
          <AlphaProperties
            path={path}
            tabIndex={currentActiveTab}
            indexChild={currentChildActiveTab}
            isMultiTab={isMultiTab}
            isSimulate={true}
            id="alphas-properties"
          />
        </div>
      );
    } else if (data === null || data === undefined) {
      return (
        <div className="simulation__results">
          <div className="simulation__progress">
            <div className="simulation__cancel">
              <FormattedMessageHelper
                tagName="span"
                id="simulationVisualization.4"
                defaultMessage={localeData.en['simulationVisualization.4']}
              />
            </div>
          </div>
          <AlphaProperties
            path={path}
            tabIndex={currentActiveTab}
            indexChild={currentChildActiveTab}
            isMultiTab={isMultiTab}
            isSimulate={true}
            id="alphas-properties"
          />
        </div>
      );
    } else {
      return (
        <div className="simulation">
          <div id="alphas-details-sections__content" className="alphas-details-sections__content">
            <AlphasDetailsContent
              path={path}
              showAlphaCode={false}
              itemsStoredName="simulation-results-order"
              showCode={showCode}
              showLearn={showLearn}
              shouldResize={shouldResize}
              isSimulation={true}
              modifiers={['full-h']}
              isSimulate={true}
              tabIndex={currentActiveTab}
              indexChild={currentChildActiveTab}
              isMultiTab={isMultiTab}
              size={this.props.size}
            />
          </div>
          <div className="alphas__footer-messages">
            <div id="alphas-details-content__content" className="alphas__message">
              <FlashMessages location="simulation-alphas" />
            </div>
          </div>
        </div>
      );
    }
  }
}

const mapStateToProps = (state: StateType): SimulationVisualizationStateProps => ({
  flashMessages: flashMessagesSimulationAlphasMessageSelector(state),
  activeTab: activeTabIndexSelector(state),
  // @ts-ignore
  shouldScrollToAlphaProperties: alphaPropertiesShouldScrollToSelector(state),
  // @ts-ignore
  fieldOnFocus: alphaPropertiesFieldOnFocusSelector(state),
  alphasFromSelectionCode: alphasSelectionCodeSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<any>): SimulationVisualizationDispatchProps => ({
  dispatchSetFieldOnFocus: (name: string | null) => dispatch(setFieldOnFocus(name)),
});

export default connect<SimulationVisualizationStateProps, DispatchConnect<SimulationVisualizationDispatchProps>, any, StateType>(
  mapStateToProps,
  mapDispatchToProps
)(SimulationVisualization);
